import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { CompanyFormValues, companyFormSchema } from "./companySettingsSchema"
import { useEffect, useRef } from "react"

// Week day options for the form
const WEEK_DAYS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' }
] as const;

interface CompanySettingsFormProps {
  defaultValues: Partial<CompanyFormValues>
  isLoading: boolean
  onSubmit: (data: CompanyFormValues) => Promise<void>
}

export function CompanySettingsForm({ defaultValues, isLoading, onSubmit }: CompanySettingsFormProps) {
  // Track if the form has been initialized with default values
  const isInitialized = useRef(false);
  
  // Initialize the form with proper type coercion for number fields
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues,
    mode: 'onChange' // Validate on change instead of on submit to provide immediate feedback
  })
  
  // Only reset the form when defaultValues change and form hasn't been initialized yet
  useEffect(() => {
    if (defaultValues && typeof defaultValues === 'object' && 
        Object.keys(defaultValues).length > 0 && !isInitialized.current) {
      form.reset(defaultValues);
      isInitialized.current = true;
    }
  }, [defaultValues, form]);
  
  // Handle form submission
  const onFormSubmit = async (data: CompanyFormValues) => {
    await onSubmit(data);
    // Don't reset isInitialized after submit to prevent form from clearing
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-6">
        {/* Company Settings Section */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Organization Name</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="e.g., Acme Corporation" 
                      {...field}
                      value={field.value || ''}
                      className="h-10 text-sm"
                    />
                  </FormControl>
                  <FormDescription className="text-xs text-muted-foreground">
                    Appears on reports and documents
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="maxOvertimeHours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Monthly Overtime Limit</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="104" 
                      {...field}
                      value={field.value || ''}
                      className="h-10 text-sm"
                    />
                  </FormControl>
                  <FormDescription className="text-xs text-muted-foreground">
                    Max hours per employee per month
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="weekStartDay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">Work Week Begins</FormLabel>
                  <FormControl>
                    <select 
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      {...field}
                      value={field.value || 'monday'}
                    >
                      {WEEK_DAYS.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription className="text-xs text-muted-foreground">
                    First day of work week
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="pt-2">
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={isLoading}
              size="default"
              variant="secondary"
              className="px-6 h-10 text-sm font-medium"
            >
              {isLoading ? "Saving Changes..." : "Save changes"}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
